import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'api/axiosConfig';
import API_BASE from "configs/hostConfig";
import {
  Box,
  Button,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  VStack,
  FormErrorMessage,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'; // Icons for password toggle
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import BackgroundWrapper from 'components/Background/Background';

const PasswordUpdate = () => {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const validationSchema = Yup.object({
    oldPassword: Yup.string().required('Old password is required'),
    newPassword: Yup.string()
      .min(6, 'New password must be at least 6 characters')
      .required('New password is required'),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
      .required('Confirm new password is required'),
  });

  const handleSubmit = (values, actions) => {
    // console.log('Password Update values:', values);
    api
      .post(`${API_BASE}/api/auth/update-password`, values)
      .then((res) => {
        if (res.status == 200) {
          navigate('/signin');
        }
      })
      .catch(() =>
        console.log('failed to update password with token: ', values.token)
      )
      .finally(() => actions.setSubmitting(false));
  };

  return (
    <BackgroundWrapper applyMd={true}>
      <Box
        bg="white"
        p={8}
        boxShadow="lg"
        borderRadius="lg"
        width="400px"
        maxWidth="90%"
        mx="auto"
        my={12}
      >
        <Heading as="h3" size="md" textAlign="center" mb={6}>
          Update Password
        </Heading>

        <Formik
          initialValues={{
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <VStack spacing={4}>
                {/* Old Password Field */}
                <Field name="oldPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.oldPassword && form.touched.oldPassword
                      }
                    >
                      <FormLabel>Old Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showOldPassword ? 'text' : 'password'}
                          placeholder="Enter your old password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={
                              showOldPassword ? <ViewOffIcon /> : <ViewIcon />
                            }
                            variant="ghost"
                            aria-label="Toggle Old Password Visibility"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.oldPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* New Password Field */}
                <Field name="newPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.newPassword && form.touched.newPassword
                      }
                    >
                      <FormLabel>New Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="Enter your new password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={
                              showNewPassword ? <ViewOffIcon /> : <ViewIcon />
                            }
                            variant="ghost"
                            aria-label="Toggle New Password Visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.newPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Confirm New Password Field */}
                <Field name="confirmNewPassword">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={
                        form.errors.confirmNewPassword &&
                        form.touched.confirmNewPassword
                      }
                    >
                      <FormLabel>Confirm New Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showConfirmNewPassword ? 'text' : 'password'}
                          placeholder="Confirm your new password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={
                              showConfirmNewPassword ? (
                                <ViewOffIcon />
                              ) : (
                                <ViewIcon />
                              )
                            }
                            variant="ghost"
                            aria-label="Toggle Confirm New Password Visibility"
                            onClick={() =>
                              setShowConfirmNewPassword(!showConfirmNewPassword)
                            }
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.confirmNewPassword}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Update Password Button */}
                <Button
                  colorScheme="green"
                  width="full"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Update Password
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>
    </BackgroundWrapper>
  );
};

export default PasswordUpdate;
